"use client";

import { Users } from "lucide-react";
import { useTranslations } from "next-intl";

import { useProjectSupporters } from "@/app/[locale]/projects/_api/use-project-queries";
import { GlassCard } from "@/components/glass-card";
import { Avatar } from "@/components/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { formatNumber } from "@/lib/utils/currency";

export function ProjectSupporters({ projectId }: { projectId: string }) {
  const t = useTranslations("projects");
  const { data: supportersPage, isLoading } = useProjectSupporters({
    id: projectId,
    query: { page: 0, size: 10 }
  });

  if (isLoading) {
    return (
      <GlassCard>
        <h3 className="text-xl font-semibold flex items-center gap-2 mb-4">
          <Users className="h-5 w-5" />
          {t("detail.supporters")}
        </h3>
        <div className="flex gap-2">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="w-10 h-10 rounded-full" />
          ))}
        </div>
      </GlassCard>
    );
  }

  const supporters = supportersPage?.content || [];
  const totalCount = supportersPage?.totalElements || 0;

  if (supporters.length === 0) {
    return (
      <GlassCard>
        <h3 className="text-xl font-semibold flex items-center gap-2 mb-4">
          <Users className="h-5 w-5" />
          {t("detail.supporters")}
        </h3>
        <p className="text-sm text-muted-foreground">{t("detail.noSupportersYet")}</p>
      </GlassCard>
    );
  }

  const displayedSupporters = supporters.slice(0, 5);
  const remainingCount = Math.max(0, totalCount - 5);

  return (
    <GlassCard>
      <h3 className="text-xl font-semibold flex items-center gap-2 mb-4">
        <Users className="h-5 w-5" />
        {t("detail.supporters")}
      </h3>
      <div className="flex items-start gap-3 flex-wrap">
        {displayedSupporters.map((supporter: { id?: string; firstName?: string; profilePictureUrl?: string }) => (
          <div key={supporter.id} className="flex flex-col items-center gap-1">
            <Avatar
              size="md"
              src={supporter.profilePictureUrl}
              name={supporter.firstName}
              title={supporter.firstName || t("detail.anonymousSupporter")}
            />
            <span className="text-xs text-muted-foreground text-center max-w-[50px] truncate">
              {supporter.firstName || t("detail.anonymous")}
            </span>
          </div>
        ))}
        {remainingCount > 0 && (
          <div className="flex flex-col items-center gap-1">
            <div className="w-10 h-10 rounded-full bg-muted border flex items-center justify-center text-xs font-medium text-muted-foreground">
              +{remainingCount}
            </div>
            <span className="text-xs text-muted-foreground text-center max-w-[50px] truncate">
              {t("detail.moreCount")}
            </span>
          </div>
        )}
      </div>
      <div className="text-xs text-muted-foreground mt-2">
        {t("detail.supporterCount", { count: formatNumber(totalCount) })}
      </div>
    </GlassCard>
  );
}
